// external
import React, { FC, useEffect, useState } from 'react';
import {
  TextField,
  Typography,
  Box,
  Paper,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Divider,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import dayjs from 'dayjs';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FitnessCenterOutlinedIcon from '@mui/icons-material/FitnessCenterOutlined';

// internal
import COETrainingDraftModal from '../coe-training-draft-modal/coe-training-draft-modal.component';
import '../coe-training-draft-modal/coe-training-draft-modal.component.css';
import TrainingDraftStepper from '../training-draft-stepper/training-draft-stepper.component';
import '../training-draft-stepper/training-draft-form.css';
import COEActionBarComponent from '../../coe-action-bar/coe-action-bar.component';
import useService from '../../../hooks/service.hook';
import TeamsService from '../../../services/teams.service';
import GroupsService from '../../../services/groups.service';
import COEMemberPanel from '../../coe-member-panel/coe-member-panel.component';
import useSnackbarHelper from '../../../hooks/snackbar.hook';
import TrainingsService from '../../../services/trainings.service';
import TrainingLibraryService from '../../../services/training-library.service';
import CustomTrainingFormsService from '../../../services/custom-training-forms.service';
import { FLOAT_VALIDATION_REGEX, INTEGER_VALIDATION_REGEX } from '../../../constants/regex.constants';
import COETrainingDurationInput from '../../coe-training-duration-input/coe-training-duration-input.component';
import {
  getFocusOptionsFromMapping,
  getSportMappings,
  getSportsOptionsFromMapping,
  getSportsTypeOptionsFromMapping,
} from '../../../helpers/app-context.helpers';
import { sortByLastName, getMeasurementUnit } from '../../../helpers/general.helpers';
import { Group, CustomTrainingForm, FieldType } from '../../../models/training.models';
import { palette } from '../../../theme';
import { FieldErrors, useFieldErrors, scrollToFirstError } from '../../form-validation';

interface EditTrainingProps {
  open: boolean;
  handleClose: any;
  handleRefresh: any;
  team: any;
  week: any;
  trainingId?: string;
  type?: 'training' | 'template';
  templateId?: string;
}

const EditTrainingComponent: FC<EditTrainingProps> = (props) => {
  const EMPTY_ASSIGN_TRAINING_FORM = {
    id: '',
    team: props.team,
    assignees: [],
    title: '',
    time: null,
    location: '',
    description: '',
    sport: '',
    sport_type: '',
    focus: '',
    published: false,
    distance: 0,
    duration: 0,
    actualTimeEnabled: true,
    attendanceEnabled: true,
    trainingType: '',
    group_id: null,
    postTrainingFormId: null as number | null,
    excludedGroupIds: [] as (string | number)[],
  };

  // hooks
  const teamsService = useService(TeamsService);
  const trainingsService = useService(TrainingsService);
  const trainingLibraryService = useService(TrainingLibraryService);
  const groupsService = useService(GroupsService);
  const customFormsService = useService(CustomTrainingFormsService);
  const { showSnackbar } = useSnackbarHelper();

  type EditTrainingField = 'title' | 'description' | 'time' | 'sport' | 'sport_type' | 'focus' | 'distance';

  // states
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [assigneeOptionsLoading, setAssigneeOptionsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');
  const { fieldErrors, setFieldErrors, clearFieldError, resetFieldErrors } = useFieldErrors<EditTrainingField>();
  const [loading, setLoading] = useState<boolean>(true);
  const [trainingForm, setTrainingForm] = useState(EMPTY_ASSIGN_TRAINING_FORM);
  const [activeTab, setActiveTab] = useState(0);
  const [visitedTabs, setVisitedTabs] = useState<Set<number>>(new Set());
  const [selectedGroup, setSelectedGroup] = useState<string | number | null>('team');
  const [groupOptions, setGroupOptions] = useState<Group[]>([]);
  const [groupsLoading, setGroupsLoading] = useState<boolean>(false);
  const sportsMappings = getSportMappings();

  // Form attachment states
  const [availableForms, setAvailableForms] = useState<CustomTrainingForm[]>([]);
  const [formsLoading, setFormsLoading] = useState(false);
  const [selectedForm, setSelectedForm] = useState<CustomTrainingForm | null>(null);
  const [formDetailsLoading, setFormDetailsLoading] = useState(false);

  // Helper function to convert Date to "HH:mm" format
  const dateToTimeString = (date: Date | null): string => {
    if (!date) return '';
    return dayjs(date).format('HH:mm');
  };

  // Helper function to convert "HH:mm" string to Date object
  const timeStringToDate = (timeString: string, baseDate?: Date): Date | null => {
    if (!timeString) return null;
    const [hours, minutes] = timeString.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return null;
    const base = baseDate ? dayjs(baseDate) : dayjs();
    return base.hour(hours).minute(minutes).second(0).millisecond(0).toDate();
  };

  // Helper function to combine date and time
  const combineDateAndTime = (date: any, time: any) => {
    if (!date && !time) return null;
    if (!date && time) {
      // If only time is provided, use today's date
      return dayjs().hour(dayjs(time).hour()).minute(dayjs(time).minute()).second(0).millisecond(0).toDate();
    }
    if (date && !time) {
      // If only date is provided, keep the existing time or use current time
      const existingTime = trainingForm.time ? dayjs(trainingForm.time) : dayjs();
      return dayjs(date).hour(existingTime.hour()).minute(existingTime.minute()).second(0).millisecond(0).toDate();
    }
    // Both date and time provided
    return dayjs(date).hour(dayjs(time).hour()).minute(dayjs(time).minute()).second(0).millisecond(0).toDate();
  };

  // Add/update Team group whenever assigneeOptions change
  useEffect(() => {
    if (assigneeOptions.length > 0) {
      setGroupOptions((prevGroups) => {
        const teamGroup: Group = {
          id: 'team',
          name: 'Team',
          members: [...assigneeOptions],
        };

        // Get existing groups (excluding Team if it exists)
        const existingGroups = prevGroups.filter((g) => g.id !== 'team');

        // Add Team group at the beginning
        return [teamGroup, ...existingGroups];
      });
    }
  }, [assigneeOptions]);

  // Fetch data when modal opens or IDs change
  useEffect(() => {
    if (props.open) {
      // Only load groups and athletes for trainings, not templates
      if (props.type !== 'template') {
        // Load groups
        if (props.team?.groups && props.team.groups.length > 0) {
          const groupsWithoutTeam = props.team.groups.filter((g) => g.id !== 'team');
          setGroupOptions(groupsWithoutTeam);
        } else if (props.team?.id) {
          getGroups();
        }

        // Load athletes
        getAthletes();
      }

      // Load training/template data
      getTraining();

      // Load available forms (only when open to avoid firing for non-coaches)
      fetchAvailableForms();
    }
  }, [props.open, props.type, props.trainingId, props.templateId]);

  const getTraining = () => {
    setLoading(true);

    if (props.type === 'template') {
      // Fetch training template
      const templateId = props.templateId;
      if (!templateId) {
        setLoading(false);
        showSnackbar('Template ID is required', 'error');
        props.handleClose();
        return;
      }

      trainingLibraryService
        .getTrainingTemplate(props.team.id, templateId)
        .then((response) => {
          const template = response.data.data;
          setTrainingForm({
            ...trainingForm,
            id: template.id,
            title: template.title,
            time: template.time ? new Date(template.time) : null,
            location: template.location || '',
            description: template.description,
            sport: template.sport,
            sport_type: template.sport_type,
            focus: template.focus,
            assignees: [],
            published: false,
            distance: template.distance || 0,
            duration: template.duration || 0,
            actualTimeEnabled: false,
            trainingType: '',
            group_id: null,
            excludedGroupIds: [],
          });
          setLoading(false);
        })
        .catch(() => {
          showSnackbar('There was an error while getting this training template. Please try again later', 'error');
          props.handleClose();
        });
    } else {
      // Fetch regular training
      if (!props.trainingId) {
        setLoading(false);
        showSnackbar('Training ID is required', 'error');
        props.handleClose();
        return;
      }
      console.log('Training ID:', props.trainingId);
      trainingsService
        .getTrainingByTeamId(props.team.id, props.trainingId)
        .then((response) => {
          const training = response.data.data;
          const trainingGroupId = training.group_id;
          setTrainingForm({
            ...trainingForm,
            id: training.id,
            title: training.title,
            time: training.time,
            location: training.location,
            description: training.description,
            sport: training.sport,
            sport_type: training.sportType,
            focus: training.focus,
            assignees: sortByLastName(training.assignees),
            published: training.published,
            distance: training.distance,
            duration: training.duration,
            actualTimeEnabled: training.actualTimeEnabled || false,
            attendanceEnabled: training.attendanceEnabled ?? true,
            trainingType: training.training_type,
            group_id: trainingGroupId,
            postTrainingFormId: training.postTrainingFormId || null,
            excludedGroupIds: training.excludedGroupIds || [],
          });
          setSelectedGroup(trainingGroupId ?? 'team');
          // If form is attached, fetch its details
          if (training.postTrainingFormId) {
            fetchFormDetails(training.postTrainingFormId);
          }
          setLoading(false);
        })
        .catch(() => {
          showSnackbar('There was an error while getting this training. Please try again later', 'error');
          props.handleClose();
        });
    }
  };

  /**
   * Fetches available forms for the team
   */
  const fetchAvailableForms = () => {
    setFormsLoading(true);
    customFormsService
      .listForms(props.team.id as string)
      .then((response) => {
        setAvailableForms(response.data.data || []);
        setFormsLoading(false);
      })
      .catch(() => {
        showSnackbar('Error loading custom training forms', 'error');
        setFormsLoading(false);
      });
  };

  /**
   * Fetches full details of a selected form
   */
  const fetchFormDetails = (formId: number) => {
    setFormDetailsLoading(true);
    customFormsService
      .getForm(props.team.id as string, formId)
      .then((response) => {
        setSelectedForm(response.data.data);
        setFormDetailsLoading(false);
      })
      .catch(() => {
        showSnackbar('Error loading form details', 'error');
        setFormDetailsLoading(false);
        setSelectedForm(null);
      });
  };

  /**
   * Handles form selection change
   */
  const handleFormSelect = (formId: number | null) => {
    setTrainingForm((values) => ({ ...values, postTrainingFormId: formId }));
    if (formId) {
      fetchFormDetails(formId);
    } else {
      setSelectedForm(null);
    }
  };

  /**
   * Grabs all members of the selected team. Returns to the home page on error.
   *
   */
  const getAthletes = () => {
    setAssigneeOptionsLoading(true);
    const teamId = (trainingForm.team as any)?.id ?? props.team?.id;
    if (teamId) {
      teamsService
        .getAllTeamMembers(teamId as string)
        .then((response) => {
          const members = Array.isArray(response.data.data) ? response.data.data : [];
          const sortedMembers = sortByLastName(members);
          setAssigneeOptions(sortedMembers);
          setAssigneeOptionsLoading(false);
        })
        .catch(() => {
          setAssigneeOptionsLoading(false);
          showSnackbar("An error occured while grabbing your teams' athletes. Please try again later", 'error');
          props.handleClose();
        });
    } else {
      setAssigneeOptionsLoading(false);
    }
  };

  /**
   * Fetches groups for the team if not already available
   */
  const getGroups = () => {
    setGroupsLoading(true);
    if (props.team?.id) {
      groupsService
        .getGroups(props.team.id)
        .then((response) => {
          const groupsWithoutTeam = (response.data.data || []).filter((g) => g.id !== 'team');
          setGroupOptions(groupsWithoutTeam);
          setGroupsLoading(false);
        })
        .catch(() => {
          setGroupsLoading(false);
          // Don't show error snackbar for groups, just set empty array
          setGroupOptions([]);
        });
    }
  };

  /**
   * Handles member selection changes
   */
  const handleMembersChange = (members: any[]) => {
    setTrainingForm((values) => ({ ...values, assignees: members }));
  };

  /**
   * Handles group selection changes and auto-selects/deselects group members
   */
  const handleGroupsChange = (groupId: string | number | null) => {
    let updatedAssignees: any[] = [];

    // Get the previously selected group (before it changes)
    const previouslySelectedGroup = groupOptions.find((g) => g.id === selectedGroup);
    const previouslySelectedGroupMemberIds = new Set((previouslySelectedGroup?.members || []).map((m: any) => m.id));

    if (groupId !== null) {
      // Group is selected: remove members from previous group, add new group members, keep manually selected
      const group = groupOptions.find((g) => g.id === groupId);
      const groupMembers = group?.members || [];
      const groupMemberIds = new Set(groupMembers.map((m: any) => m.id));

      // Start with new group members
      updatedAssignees = [...groupMembers];

      // Add any manually selected members that aren't in the new group or previous group
      trainingForm.assignees.forEach((member: any) => {
        if (!groupMemberIds.has(member.id) && !previouslySelectedGroupMemberIds.has(member.id)) {
          updatedAssignees.push(member);
        }
      });
    } else {
      // Group is deselected: remove members from the previously selected group, keep manually selected members
      updatedAssignees = trainingForm.assignees.filter(
        (member: any) => !previouslySelectedGroupMemberIds.has(member.id)
      );
    }

    setSelectedGroup(groupId);

    // Determine trainingType and group_id based on selected group
    const isTeamGroup = groupId === 'team' || groupId === null;
    const trainingType = isTeamGroup ? 'team' : 'group';
    const group_id = isTeamGroup ? null : groupId;

    setTrainingForm((values) => ({
      ...values,
      assignees: sortByLastName(updatedAssignees),
      group_id: group_id,
      trainingType: trainingType,
    }));
  };

  /**
   * Handles excluded groups change and syncs assignees: remove members of newly excluded groups,
   * add members of newly un-excluded groups (mirrors handleGroupsChange behavior).
   */
  const handleExcludedGroupsChange = (ids: (string | number)[]) => {
    const prevExcluded = trainingForm.excludedGroupIds ?? [];
    const addedIds = ids.filter((id) => !prevExcluded.includes(id));
    const removedIds = prevExcluded.filter((id) => !ids.includes(id));

    let updatedAssignees = [...trainingForm.assignees];

    for (const groupId of addedIds) {
      const group = groupOptions.find((g) => g.id === groupId);
      if (!group?.members?.length) continue;
      const memberIdsToRemove = new Set(group.members.map((m: any) => m.id));
      updatedAssignees = updatedAssignees.filter((m: any) => !memberIdsToRemove.has(m.id));
    }

    const existingAssigneeIds = new Set(updatedAssignees.map((m: any) => m.id));
    for (const groupId of removedIds) {
      const group = groupOptions.find((g) => g.id === groupId);
      if (!group?.members?.length) continue;
      for (const member of group.members) {
        if (!existingAssigneeIds.has(member.id)) {
          updatedAssignees.push(member);
          existingAssigneeIds.add(member.id);
        }
      }
    }

    setTrainingForm((prev) => ({
      ...prev,
      excludedGroupIds: ids,
      assignees: sortByLastName(updatedAssignees),
    }));
  };

  /**
   * Validates the training form, collecting all field errors in one pass.
   * Returns true when the form is valid.
   */
  const validateForm = (): boolean => {
    const errors: FieldErrors<EditTrainingField> = {};

    if (trainingForm.title.length < 1) {
      errors.title = 'Title is required';
    }

    if (trainingForm.time === null) {
      errors.time = 'Date is required';
    } else if ((trainingForm.time as any).toString() === 'Invalid Date') {
      errors.time = 'Date is invalid';
    }

    if (trainingForm.description.length < 1) {
      errors.description = 'Description is required';
    }

    if (trainingForm.sport.length < 1) {
      errors.sport = 'Sport is required';
    }

    if (trainingForm.sport_type.length < 1) {
      errors.sport_type = 'Sport type is required';
    }

    if (trainingForm.focus.length < 1) {
      errors.focus = 'Focus is required';
    }

    if (
      !FLOAT_VALIDATION_REGEX.test(trainingForm.distance as any) &&
      !INTEGER_VALIDATION_REGEX.test(trainingForm.distance as any)
    ) {
      errors.distance = 'Distance planned contains invalid characters';
    }

    setFieldErrors(errors);
    scrollToFirstError('edit-draft-field', errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Submits the training and the members to assign it to
   *
   */
  const handleSubmit = () => {
    setSubmitError('');
    resetFieldErrors();
    if (validateForm()) {
      setLoading(true);

      if (props.type === 'template') {
        const templateId = props.templateId;
        if (!templateId) {
          setLoading(false);
          setSubmitError('Template ID is required');
          return;
        }

        const payload = convertTemplateFormToPayload(trainingForm);
        trainingLibraryService
          .updateTrainingTemplate(props.team.id, templateId, payload)
          .then(() => {
            showSnackbar('Successfully updated training template', 'success');
            props.handleRefresh();
            props.handleClose();
          })
          .catch(() => {
            setLoading(false);
            setSubmitError('Error while updating this training template.');
          });
      } else {
        if (!props.trainingId) {
          setLoading(false);
          setSubmitError('Training ID is required');
          return;
        }

        const payload = convertAssignTrainingFormToPayload(trainingForm);
        trainingsService
          .updateTrainingByTeamId(props.team.id, props.trainingId, payload)
          .then(() => {
            showSnackbar('Successfully updated training', 'success');
            props.handleRefresh();
            props.handleClose();
          })
          .catch(() => {
            setLoading(false);
            setSubmitError('Error while updating this training.');
          });
      }
    }
  };

  /**
   * Converts the form into the proper assign training payload
   *
   */
  const convertAssignTrainingFormToPayload = (formData: any) => {
    return {
      assignees: formData.assignees.map((assignee: any) => assignee.id),
      training: {
        title: formData.title,
        time: dayjs(formData.time).format(),
        location: formData.location,
        description: formData.description,
        sport: formData.sport,
        sportType: formData.sport_type,
        focus: formData.focus,
        duration: formData.duration,
        distance: formData.distance,
        actualTimeEnabled: formData.actualTimeEnabled,
        attendanceEnabled: formData.attendanceEnabled,
        trainingType: formData.trainingType,
        postTrainingFormId: formData.postTrainingFormId,
        ...(formData.group_id ? { group_id: formData.group_id } : {}),
        excludedGroupIds: formData.excludedGroupIds || [],
      },
    };
  };

  /**
   * Converts the form into the proper template payload (only template-specific fields)
   *
   */
  const convertTemplateFormToPayload = (formData: any) => {
    return {
      title: formData.title,
      description: formData.description,
      time: dayjs(formData.time).format(),
      location: formData.location,
      sport: formData.sport,
      sport_type: formData.sport_type,
      focus: formData.focus,
      duration: formData.duration,
      distance: formData.distance,
    };
  };

  const isTemplate = props.type === 'template';

  // Field type labels and colors for display
  const FIELD_TYPE_LABELS: Record<FieldType, string> = {
    text: 'Text',
    number: 'Number',
    enum: 'Dropdown',
    rating: 'Rating',
    full_team_number: 'Per-Athlete',
    target_recording: 'Target Recording',
  };

  const FIELD_TYPE_COLORS: Record<
    FieldType,
    'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
  > = {
    text: 'default',
    number: 'info',
    enum: 'secondary',
    rating: 'warning',
    full_team_number: 'success',
    target_recording: 'primary',
  };

  /**
   * Renders the forms tab content
   */
  const renderFormsTab = () => {
    return (
      <Box className="training-draft-forms-pane">
        <FormControl fullWidth size="small" sx={{ mb: 2, textAlign: 'left' }}>
          <Select
            displayEmpty
            value={trainingForm.postTrainingFormId || ''}
            onChange={(e) => handleFormSelect(e.target.value ? Number(e.target.value) : null)}
            disabled={formsLoading}
            sx={{ borderRadius: '12px', background: '#fff' }}
          >
            <MenuItem value="">
              <em>Select Post-Training Form (Optional)</em>
            </MenuItem>
            {availableForms.map((form) => (
              <MenuItem key={form.id} value={form.id}>
                {form.name} ({form.fields.length} fields)
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {formsLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={40} />
          </Box>
        )}

        {!trainingForm.postTrainingFormId && !formsLoading && (
          <Box className="training-draft-forms-empty">
            <DescriptionOutlinedIcon sx={{ fontSize: 40, color: palette.grey[300], mb: 1.25 }} />
            <Typography className="training-draft-forms-empty__title">No Form Attached</Typography>
            <Typography className="training-draft-forms-empty__desc">
              Select a form above to collect data after this training session
            </Typography>
          </Box>
        )}

        {trainingForm.postTrainingFormId && formDetailsLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={40} />
          </Box>
        )}

        {trainingForm.postTrainingFormId && selectedForm && !formDetailsLoading && (
          <Paper variant="outlined" sx={{ mt: 1, p: 2, textAlign: 'left', borderRadius: '12px' }}>
            <Typography variant="h6" gutterBottom>
              {selectedForm.name}
            </Typography>
            {selectedForm.description && (
              <Typography variant="body2" color="textSecondary" paragraph>
                {selectedForm.description}
              </Typography>
            )}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Form Fields ({selectedForm.fields.length})
            </Typography>
            <Box sx={{ mt: 2 }}>
              {selectedForm.fields.map((field, index) => (
                <Box
                  key={field.field_id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1.5,
                    mb: 1,
                    bgcolor: palette.grey[100],
                    borderRadius: 1,
                  }}
                >
                  <Box
                    sx={{
                      minWidth: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                      mr: 2,
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2">{field.name}</Typography>
                  </Box>
                  <Chip
                    label={FIELD_TYPE_LABELS[field.field_type]}
                    size="small"
                    color={FIELD_TYPE_COLORS[field.field_type]}
                  />
                </Box>
              ))}
            </Box>
          </Paper>
        )}
      </Box>
    );
  };

  return (
    <COETrainingDraftModal
      open={props.open}
      onClose={props.handleClose}
      title={isTemplate ? 'Edit Training Template' : 'Edit Training'}
      subtitle={
        isTemplate ? 'Update this saved session in your training library' : 'Update session details, members, and forms'
      }
      badge={isTemplate ? undefined : trainingForm.published ? 'LIVE' : 'DRAFT'}
      badgeTone={trainingForm.published ? 'live' : 'draft'}
      gradientHeader
      headerIcon={
        isTemplate ? (
          <FitnessCenterOutlinedIcon sx={{ fontSize: 20, color: '#fff' }} />
        ) : (
          <EditOutlinedIcon sx={{ fontSize: 20, color: '#fff' }} />
        )
      }
      error={
        submitError || (Object.keys(fieldErrors).length > 0 ? 'Please fix the highlighted fields below' : undefined)
      }
      loading={loading}
      actions={[
        {
          title: 'Update',
          color: 'primary',
          variant: 'contained',
          onClick: () => handleSubmit(),
        },
      ]}
    >
      <div className="training-draft-form-container">
        <div className="training-draft-form-fields">
          <TextField
            id="edit-draft-field-title"
            placeholder="Title *"
            name="title"
            required
            value={trainingForm.title}
            onChange={(event) => {
              setTrainingForm((values) => ({ ...values, title: event.target.value }));
              clearFieldError('title');
            }}
            variant="outlined"
            margin="none"
            fullWidth
            error={!!fieldErrors.title}
            helperText={fieldErrors.title || ''}
            sx={{
              '& .MuiInputBase-input': { fontWeight: 600, fontSize: '14px', padding: '14px' },
            }}
          />
          <TextField
            id="edit-draft-field-description"
            placeholder="Description *"
            multiline
            required
            margin="none"
            minRows={3}
            maxRows={10}
            value={trainingForm.description}
            error={!!fieldErrors.description}
            helperText={fieldErrors.description || ''}
            onChange={(event) => {
              setTrainingForm((values) => ({ ...values, description: event.target.value }));
              clearFieldError('description');
            }}
            onPaste={(event) => {
              event.preventDefault();
              const plain = event.clipboardData.getData('text/plain');
              const normalized = plain
                .replace(/\r\n/g, '\n')
                .replace(/\r/g, '\n')
                .replace(/<[^>]*>/g, '');
              const target = event.target as HTMLTextAreaElement;
              const start = target.selectionStart ?? trainingForm.description.length;
              const end = target.selectionEnd ?? trainingForm.description.length;
              const next = trainingForm.description.slice(0, start) + normalized + trainingForm.description.slice(end);
              setTrainingForm((values) => ({ ...values, description: next }));
            }}
            sx={{ '& .MuiInputBase-input': { fontSize: '14px', padding: '12px' } }}
          />
          <div className="training-draft-row">
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <TextField
                id="edit-draft-field-time"
                type="time"
                value={dateToTimeString(trainingForm.time)}
                onChange={(event) => {
                  const timeDate = timeStringToDate(event.target.value, trainingForm.time);
                  const combined = combineDateAndTime(trainingForm.time, timeDate);
                  setTrainingForm((values) => ({ ...values, time: combined }));
                  clearFieldError('time');
                }}
                margin="none"
                fullWidth
                error={!!fieldErrors.time}
                helperText={fieldErrors.time || ''}
                inputProps={{ step: 300 }}
                InputProps={{
                  endAdornment: <AccessTimeIcon sx={{ fontSize: 16, color: palette.grey[400] }} />,
                }}
                sx={{ '& .MuiInputBase-input': { fontWeight: 600, fontSize: '14px' } }}
              />
              <DatePicker
                value={trainingForm.time}
                onChange={(date: any) => {
                  const combined = combineDateAndTime(date, trainingForm.time);
                  setTrainingForm((values) => ({
                    ...values,
                    time: combined,
                  }));
                }}
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    margin="none"
                    fullWidth
                    placeholder="Date"
                    sx={{ '& .MuiInputBase-input': { fontWeight: 600, fontSize: '14px' } }}
                  />
                )}
              />
            </LocalizationProvider>
            <TextField
              placeholder="Location"
              name="location"
              value={trainingForm.location}
              onChange={(event) =>
                setTrainingForm((values) => ({
                  ...values,
                  location: event.target.value,
                }))
              }
              variant="outlined"
              margin="none"
              fullWidth
              sx={{ '& .MuiInputBase-input': { fontWeight: 600, fontSize: '14px' } }}
            />
          </div>
          <div className="training-draft-attendance-strip">
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={trainingForm.attendanceEnabled}
                  onChange={(e) => setTrainingForm((values) => ({ ...values, attendanceEnabled: e.target.checked }))}
                />
              }
              label="Track attendance"
              labelPlacement="end"
            />
          </div>
        </div>

        <div className="training-draft-steps-shell">
          <TrainingDraftStepper
            steps={
              isTemplate
                ? [{ label: 'Training Details' }]
                : [{ label: 'Training Details' }, { label: 'Members & Groups' }, { label: 'Forms' }]
            }
            activeStep={activeTab}
            completedSteps={visitedTabs}
            onStepChange={(index) => {
              setVisitedTabs((prev) => new Set(prev).add(activeTab));
              setActiveTab(index);
            }}
          />

          {activeTab === 0 && (
            <div className="training-draft-details-pane">
              <div className="training-draft-section" id="edit-draft-field-sport">
                <span
                  className="training-draft-section-title"
                  style={{ color: fieldErrors.sport ? palette.error.main : undefined }}
                >
                  Sport{fieldErrors.sport ? ` — ${fieldErrors.sport}` : ''}
                </span>
                <COEActionBarComponent
                  items={getSportsOptionsFromMapping(sportsMappings)}
                  selectedItem={trainingForm.sport}
                  setSelectedItem={(item: any) => {
                    setTrainingForm((values) => ({ ...values, sport: item }));
                    clearFieldError('sport');
                  }}
                  padding={'3px 10px'}
                  fontSize={14}
                  marginTop={0}
                />
              </div>
              <div
                className="training-draft-section"
                id="edit-draft-field-sport_type"
                style={{ display: trainingForm.sport ? 'block' : 'none' }}
              >
                <span
                  className="training-draft-section-title"
                  style={{ color: fieldErrors.sport_type ? palette.error.main : undefined }}
                >
                  Sport Type{fieldErrors.sport_type ? ` — ${fieldErrors.sport_type}` : ''}
                </span>
                <COEActionBarComponent
                  items={getSportsTypeOptionsFromMapping(sportsMappings, trainingForm.sport)}
                  selectedItem={trainingForm.sport_type}
                  setSelectedItem={(item: any) => {
                    setTrainingForm((values) => ({ ...values, sport_type: item }));
                    clearFieldError('sport_type');
                  }}
                  padding={'3px 10px'}
                  fontSize={14}
                  marginTop={0}
                />
              </div>
              <div className="training-draft-section" id="edit-draft-field-focus">
                <span
                  className="training-draft-section-title"
                  style={{ color: fieldErrors.focus ? palette.error.main : undefined }}
                >
                  Focus{fieldErrors.focus ? ` — ${fieldErrors.focus}` : ''}
                </span>
                <COEActionBarComponent
                  items={getFocusOptionsFromMapping(sportsMappings, trainingForm.sport)}
                  selectedItem={trainingForm.focus}
                  setSelectedItem={(item: any) => {
                    setTrainingForm((values) => ({ ...values, focus: item }));
                    clearFieldError('focus');
                  }}
                  padding={'3px 10px'}
                  fontSize={14}
                  marginTop={0}
                />
              </div>
              <div className="training-draft-total-row" id="edit-draft-field-distance">
                <span className="training-draft-total-row__label">Total</span>
                <div className="training-draft-total-row__box">
                  <AccessTimeIcon sx={{ fontSize: 14, color: palette.grey[400] }} />
                  <COETrainingDurationInput
                    value={trainingForm.duration as any}
                    onChange={(seconds) => {
                      setTrainingForm((values: any) => ({ ...values, duration: seconds }));
                    }}
                  />
                </div>
                <div className="training-draft-total-row__box">
                  <PlaceOutlinedIcon sx={{ fontSize: 14, color: palette.grey[400] }} />
                  <TextField
                    size="small"
                    type="number"
                    value={trainingForm.distance}
                    onChange={(evt) => {
                      setTrainingForm((values: any) => ({ ...values, distance: evt.target.value }));
                      clearFieldError('distance');
                    }}
                    error={!!fieldErrors.distance}
                    helperText={fieldErrors.distance || ''}
                    InputProps={{
                      endAdornment: (
                        <Typography variant="caption" sx={{ color: palette.text.secondary }}>
                          {getMeasurementUnit()}
                        </Typography>
                      ),
                    }}
                    sx={{ width: 80 }}
                  />
                </div>
              </div>
            </div>
          )}
          {!isTemplate && activeTab === 1 && (
            <COEMemberPanel
              members={assigneeOptions}
              selectedMembers={trainingForm.assignees}
              onMembersChange={handleMembersChange}
              groups={groupOptions}
              selectedGroup={selectedGroup}
              onGroupsChange={handleGroupsChange}
              membersLoading={assigneeOptionsLoading}
              groupsLoading={groupsLoading}
              excludedGroupIds={trainingForm.excludedGroupIds ?? []}
              onExcludedGroupsChange={handleExcludedGroupsChange}
            />
          )}
          {!isTemplate && activeTab === 2 && renderFormsTab()}
        </div>
      </div>
    </COETrainingDraftModal>
  );
};

export default EditTrainingComponent;
